import { useQuery } from '@apollo/client';
import { AddCircle, Stadium, Sync } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useSyncEvents } from '~/api/events/sync';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { Count } from '~/components/count';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { SearchBar } from '~/components/forms/search-bar';
import { PageContainer } from '~/components/page-layout';
import { SearchContainer } from '~/components/search';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { NetworkEventTypeEventsDocument } from '../queries/events.generated';
import { BulkActions } from './components/bulk-actions';
import { EventsTable } from './components/events-table';
import { NewEventDialog } from './components/new-event-dialog';
import { useTabs } from './lib/tabs';

export const EventTypeEvents = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const link = useLink();

  const tabs = useTabs();

  const [syncing, setSyncing] = useState(false);

  const [newEventOpen, setNewEventOpen] = useState(false);

  const [confirm, confirmProps] = useConfirmDialog();

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const params = useParams<{ eventTypeId: string }>();

  const eventTypeId = Number(params.eventTypeId);

  const eventsQuery = useQuery(NetworkEventTypeEventsDocument, {
    variables: { networkId: currentNetwork.id, eventTypeId, search },
  });

  const { data, loading } = eventsQuery;

  const events = data?.network?.eventType.events ?? [];

  const [syncEvents] = useSyncEvents();

  if (!loading && !data?.network?.eventType) return <Navigate to={link('/not-found')} replace />;

  const actions = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title={t('pages.event_types.events_tab.new_event_tooltip')}>
        <span>
          <Button
            color="primary"
            startIcon={<AddCircle />}
            variant="contained"
            onClick={() => setNewEventOpen(true)}
          >
            {t('pages.event_types.events_tab.new_event_button')}
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          data?.network?.eventType.canSync.value
            ? t('pages.event_types.events_tab.sync_tooltip_enabled')
            : t('pages.event_types.events_tab.sync_tooltip_disabled', {
                reason: data?.network?.eventType.canSync.reasons?.fullMessages[0] ?? '',
              })
        }
      >
        <span>
          <Button
            disabled={!data?.network?.eventType.canSync.value}
            color="primary"
            startIcon={<Sync />}
            variant="contained"
            onClick={async () => {
              if (!(await confirm())) return;
            }}
          >
            {t('pages.event_types.events_tab.sync_button')}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <>
      {data?.network && (
        <>
          <Toolbar
            actions={actions}
            breadcrumbsLabel={<RouterBreadcrumbs />}
            titleIcon={<Stadium />}
            titleText={data.network.eventType.name}
          />

          <ScrollableRouterTabs current={t('common.tabs.events')} tabs={tabs} />

          <PageContainer>
            <SearchContainer>
              <SearchBar
                placeholder={t('pages.event_types.events_tab.search_placeholder')}
                search={search}
              />
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                {selectedIds.length > 0 ? (
                  <BulkActions eventType={data.network.eventType} selectedIds={selectedIds} />
                ) : null}
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={events.length}
                  thing="common.pluralize.event_plural"
                />
              </Box>
            </SearchContainer>
            <EventsTable
              checkboxSelection={events.length > 1}
              disableRowSelectionOnClick={events.length < 2}
              loading={loading}
              onRowSelectionModelChange={setSelectedIds}
              eventType={data.network.eventType}
              rows={events}
              rowSelectionModel={selectedIds}
            />
          </PageContainer>
          <ConfirmDialog
            {...confirmProps}
            confirmButton={
              <LoadingButton
                loadingPosition="start"
                loading={syncing}
                color="primary"
                startIcon={<Sync />}
                variant="contained"
                onClick={async () => {
                  setSyncing(true);

                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  await syncEvents({ variables: { eventTypeId: data.network!.eventType.id } });

                  // Delay to give the que job enough time to process
                  await new Promise((resolve) => setTimeout(resolve, 3000));

                  // Refetch here after the job has had long enough to finish (we hope)
                  await eventsQuery.refetch();

                  setSyncing(false);

                  confirmProps.onConfirm();
                }}
              >
                {t('pages.event_types.events_tab.sync_confirm_button')}
              </LoadingButton>
            }
            prompt={t('pages.event_types.events_tab.sync_confirm_prompt')}
            title={t('pages.event_types.events_tab.sync_confirm_title')}
          />
        </>
      )}

      {data?.network?.eventType && (
        <NewEventDialog
          close={() => setNewEventOpen(false)}
          eventTypes={[data.network.eventType]}
          open={newEventOpen}
        />
      )}
    </>
  );
};
